import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import moment from 'moment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CryptoTracker = ({ onClose }) => {
  const [date, setDate] = useState(0);
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const xAxisMarks = 7;
  const timestampOneDay = 60 * 60 * 24;
  const timestampOneWeek = timestampOneDay * 7;
  const timestampOneMonth = timestampOneWeek * 4;

  const dateSelections = [
    { value: 0, text: `Last ${xAxisMarks} days` },
    { value: 1, text: `Last ${xAxisMarks} weeks` },
    { value: 2, text: 'Last 28 weeks' }
  ];

  const dateTimestampMapping = {
    0: timestampOneDay,
    1: timestampOneWeek,
    2: timestampOneMonth
  };

  useEffect(() => {
    initDependencies();
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const initDependencies = async () => {
    setLoading(true);
    setData([]);

    try {
      const timestamps = getTimestamps();
      const promises = getPromises(timestamps);
      const responses = await Promise.all(promises);
      
      const newData = [];
      for (let i = 0; i < responses.length; i++) {
        newData.unshift(responses[i].data.BTC.USD);
      }
      setData(newData);
    } catch (error) {
      console.error('Error fetching crypto data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const getPromises = (timestamps) => {
    const promises = [];
    for (let i = 0; i < timestamps.length; i++) {
      promises.push(getCryptoPrices(timestamps[i]));
    }
    return promises;
  };

  const getTimestamps = () => {
    const timestampList = [];
    let currentTimestamp = moment().unix();
    const timeMeasurement = dateTimestampMapping[date];

    for (let i = 0; i < xAxisMarks; i++) {
      timestampList.push(currentTimestamp);
      currentTimestamp -= timeMeasurement;
    }

    getLabels(timestampList);
    return timestampList;
  };

  const getLabels = (timestamps) => {
    const newLabels = [];
    for (let i = 0; i < timestamps.length; i++) {
      newLabels.unshift(moment.unix(timestamps[i]).format('MM-DD'));
    }
    setLabels(newLabels);
  };

  const getCryptoPrices = (timestamp) => {
    return axios.get(`https://min-api.cryptocompare.com/data/pricehistorical?fsym=BTC&tsyms=USD&ts=${timestamp}`);
  };

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: 'Bitcoin Price (USD)',
        data: data,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Bitcoin Price History'
      }
    }
  };

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="dateRange" className="form-label">Date Range:</label>
        <select
          className="form-select"
          id="dateRange"
          value={date}
          onChange={(e) => setDate(parseInt(e.target.value))}
        >
          {dateSelections.map((selection) => (
            <option key={selection.value} value={selection.value}>
              {selection.text}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="chart-container">
          <Line data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};

export default CryptoTracker;
